/**
 * eventRiskMultipliers.js — 08/02/2026 (Q2 Lot D user prod)
 *
 * Multiplicateurs de risque par catégorie d'événement, pour pondérer la
 * probabilité de tirage dans `generateRandomEvent`. Pattern identique à
 * `breakdownRisk.computeBreakdownRiskMultiplier` mais étendu aux
 * domaines :
 *
 *   • SOCIAL / GRÈVE — corrélé à la satisfaction moyenne des chauffeurs
 *     et à la présence de tempéraments forts (rebelle, fier). Atténué
 *     par Nadia Cheikh (DRH).
 *
 *   • ADMINISTRATIF — atténué par la présence de fonctions support
 *     (avocat-conseil, DAF, attachée presse) et l'investissement
 *     « Sauvegarde + audit informatique » (traçabilité préservée).
 *
 * Tous les multiplicateurs sont neutres à 1.0 ; > 1 = risque accru,
 * < 1 = risque atténué. Plafond min 0.20 pour ne JAMAIS annuler le
 * risque (le jeu doit rester imprévisible).
 */

// Tempéraments qui augmentent la probabilité de grève individuelle.
// Cohérent avec data/personalityTraits.js.
const HOT_TEMPERED_TRAITS = new Set(['rebelle', 'fier']);

/**
 * Multiplicateur social. 1.0 = baseline (équipe à 60 % de satisfaction,
 * aucun tempérament fort, sans Nadia Cheikh).
 *
 *   • Satisfaction moyenne ≥ 80 % → ×0.50 (équipe très satisfaite)
 *   • 65-79 % → ×0.75
 *   • 45-64 % → ×1.00 (zone neutre)
 *   • 30-44 % → ×1.75
 *   • < 30 % → ×2.50 (équipe à cran)
 *   • +15 % par chauffeur fort tempérament (rebelle / fier)
 *   • Nadia Cheikh recrutée → ×0.80
 */
export function computeSocialRiskMultiplier(state) {
  const drivers = state?.drivers || [];
  if (drivers.length === 0) return 1.0;
  let mult = 1.0;

  // (1) Satisfaction moyenne
  const avgSat = drivers.reduce((s, d) => s + (d.satisfaction ?? 70), 0) / drivers.length;
  if (avgSat >= 80) mult *= 0.50;
  else if (avgSat >= 65) mult *= 0.75;
  else if (avgSat >= 45) mult *= 1.00;
  else if (avgSat >= 30) mult *= 1.75;
  else mult *= 2.50;

  // (2) Tempéraments forts
  const hotCount = drivers.filter(d =>
    Array.isArray(d.personalityTraits) && d.personalityTraits.some(t => HOT_TEMPERED_TRAITS.has(t))
  ).length;
  if (hotCount > 0) mult *= (1 + 0.15 * hotCount);

  // (3) DRH (Nadia Cheikh) atténue les conflits sociaux
  if (state?.hiredSupport?.['role-nadia-cheikh']) mult *= 0.80;

  return mult;
}

/**
 * Multiplicateur administratif. 1.0 = baseline (aucun support, pas d'audit IT).
 *
 *   • Avocate-conseil (Juliette Charron-Berthet) → ×0.75
 *   • Avocat externe (Paul Renaud) → ×0.80
 *   • DAF (Isabelle Fontaine) → ×0.85
 *   • Attachée presse (Aïcha Diallo) → ×0.90
 *   • inv-it-backup-01 (Sauvegarde + audit IT) → ×0.70
 *
 * Plafond min 0.20 (le risque ne descend jamais à 0).
 */
export function computeAdminRiskMultiplier(state) {
  let mult = 1.0;
  const hired = state?.hiredSupport || {};
  const active = state?.activeInvestments || {};

  if (hired['role-juliette-charron-berthet']) mult *= 0.75;
  if (hired['role-paul-renaud']) mult *= 0.80;
  if (hired['role-isabelle-fontaine']) mult *= 0.85;
  if (hired['role-aicha-diallo']) mult *= 0.90;
  if (active['inv-it-backup-01']) mult *= 0.70;

  return Math.max(0.20, mult);
}

/**
 * Multiplicateur additionnel pour les pannes (accidents/pannes) :
 *   • Vidéosurveillance (inv-security-01) → -50 % proba vol carburant
 *     (utilisé spécifiquement pour evt-rh-fuel-theft-detect).
 */
export function computeFuelTheftRiskMultiplier(state) {
  const active = state?.activeInvestments || {};
  let mult = 1.0;
  if (active['inv-security-01']) mult *= 0.50;
  if (active['inv-it-backup-01']) mult *= 0.85; // traçabilité IT
  return mult;
}

/**
 * Détermine si un accident est imputable au chauffeur. Utilisé pour
 * appliquer la pénalité de réputation UNIQUEMENT en cas de faute (cf.
 * Lot D user prod : « la réputation ne doit baisser que si le chauffeur
 * est en faute, pas si c'est météo/usure équipement »).
 *
 * Heuristique :
 *   • Si météo récente (breakdownBoostUntil > totalDays) → 30 % faute
 *   • Si flotte usée (avgWear > 70) → 40 % faute (équipement mauvais
 *     état, responsabilité de l'entreprise pas du chauffeur)
 *   • Si chauffeur très fatigué (avgFatigue > 70) → 70 % faute
 *   • Sinon → 50 % faute (cas standard)
 *
 * Retourne `{ atFault: boolean, reason: string }`.
 */
export function rollAccidentFault(state) {
  const today = state?.totalDays || 0;
  const weatherActive = (state?.breakdownBoostUntil || 0) > today;
  const fleet = state?.vehicles || [];
  const drivers = state?.drivers || [];
  const avgWear = fleet.length > 0
    ? fleet.reduce((s, v) => s + (v.wear || 0), 0) / fleet.length
    : 0;
  const avgFatigue = drivers.length > 0
    ? drivers.reduce((s, d) => s + (d.fatigue || 0), 0) / drivers.length
    : 0;

  let pFault = 0.50;
  let reason = 'standard';
  if (weatherActive) { pFault = 0.30; reason = 'weather'; }
  else if (avgWear > 70) { pFault = 0.40; reason = 'equipment'; }
  else if (avgFatigue > 70) { pFault = 0.70; reason = 'fatigue'; }

  const atFault = Math.random() < pFault;
  return { atFault, reason, pFault };
}
